import axios from 'axios';
import { format } from 'date-fns';
import download from 'downloadjs';
import { baseURL } from './api';

const apiVersion = 'v2';
export const path = process.env.REACT_APP_BASE_URL + '/api/';

class CallsService {
  static baseURL = baseURL;

  static resourcePath = path || '';

  constructor(api, demo = false, baseURLOverride) {
    this.api = api;
    this.demo = demo;
    this.baseURL = baseURLOverride;
  }

  getBaseURL = () => {
    return this.baseURL || CallsService.resourcePath || CallsService.baseURL;
  };

  getPaginateOptions = options => {
    return JSON.stringify(options || { page: 1, limit: 10 });
  };

  async getOutgoingCallsByCompany(
    id = null,
    paginateOptions,
    date,
    getDeleted = global.deleteMode,
  ) {
    const headers = {
      Authorization: `Bearer ${this.api.token}`,
    };

    const options = this.getPaginateOptions(paginateOptions);
    const filterDate = date === 'undefined' ? '' : `&from=${date[1]}&to=${date[0]}`;

    const { data } = await axios.get(
      id === null
        ? `${this.getBaseURL()}${apiVersion}/calls/outgoingCalls?paginateOptions=${options}${filterDate}&getDeleted=${getDeleted}`
        : `${this.getBaseURL()}${apiVersion}/companies/${id}/outgoingCalls?paginateOptions=${options}${filterDate}&getDeleted=${getDeleted}`,
      { headers },
    );

    return data;
  }

  async getOutgoingCallsByDate(filter, getDeleted = global.deleteMode) {
    const headers = {
      Authorization: `Bearer ${this.api.token}`,
    };
    const { data } = await axios.get(
      `${this.getBaseURL()}${apiVersion}/calls/outgoingCalls?from=${filter[1]}&to=${
        filter[0]
      }&getDeleted=${getDeleted}`,
      { headers },
    );
    return data;
  }

  // {{BASE_URL}}/calls/outgoingCalls/filters/?search={"filterBy":"callingNum", "value":1}&getDeleted=false
  // {{BASE_URL}}/companies/:id/outgoingCalls/filters/?search={"filterBy":"callingNum", "value":1}&getDeleted=false
  async outgoingCallsByFilter(
    filter,
    paginateOptions,
    date,
    cid = null,
    getDeleted = global.deleteMode,
  ) {
    const headers = {
      Authorization: `Bearer ${this.api.token}`,
    };
    const options = this.getPaginateOptions(paginateOptions);
    const filterValue = filter.value !== '' ? filter.value : 1;
    const filterDate = date === 'undefined' ? '' : `&from=${date[1]}&to=${date[0]}`;
    const { data } =
      cid === null
        ? await axios.get(
            `${this.getBaseURL()}${apiVersion}/calls/outgoingCalls/filters/?search={"filterBy":"${
              filter.name
            }", "value":${filterValue}}&paginateOptions=${options}${filterDate}&getDeleted=${getDeleted}`,
            { headers },
          )
        : await axios.get(
            `${this.getBaseURL()}${apiVersion}/companies/${cid}/outgoingCalls/filters/?search={"filterBy":"${
              filter.name
            }", "value":${filterValue}}&paginateOptions=${options}${filterDate}&getDeleted=${getDeleted}`,
            { headers },
          );
    return data;
  }

  async onRecoverIncomingCall(callId) {
    const headers = {
      Authorization: `Bearer ${this.api.token}`,
    };
    const data = await axios.patch(
      `${this.getBaseURL()}${apiVersion}/calls/incomingCalls/${callId}`,
      {
        deleted: false,
      },
      { headers },
    );
    return data;
  }

  async onRecoverOutgoingCall(callId) {
    const headers = {
      Authorization: `Bearer ${this.api.token}`,
    };
    const data = await axios.patch(
      `${this.getBaseURL()}${apiVersion}/calls/outgoingCalls/${callId}`,
      {
        deleted: false,
      },
      { headers },
    );
    return data;
  }

  async getIncomingCallsByDate(filter, getDeleted = global.deleteMode) {
    const headers = {
      Authorization: `Bearer ${this.api.token}`,
    };
    const { data } = await axios.get(
      `${this.getBaseURL()}${apiVersion}/calls/incomingCalls?from=${filter[1]}&to=${
        filter[0]
      }&getDeleted=${getDeleted}`,
      { headers },
    );
    return data;
  }

  // {{BASE_URL}}/calls/incomingCalls/filters/?search={"filterBy":"callingNum", "value":1}&getDeleted=false
  async incomingCallsByFilter(
    filter,
    paginateOptions,
    date,
    cid = null,
    getDeleted = global.deleteMode,
  ) {
    const headers = {
      Authorization: `Bearer ${this.api.token}`,
    };
    const options = this.getPaginateOptions(paginateOptions);
    const filterValue = filter.value !== '' ? filter.value : 1;
    const filterDate = date === 'undefined' ? '' : `&from=${date[1]}&to=${date[0]}`;
    const { data } =
      cid === null
        ? await axios.get(
            `${this.getBaseURL()}${apiVersion}/calls/incomingCalls/filters/?search={"filterBy":"${
              filter.name
            }", "value":${filterValue}}&paginateOptions=${options}${filterDate}&getDeleted=${getDeleted}`,
            { headers },
          )
        : await axios.get(
            `${this.getBaseURL()}${apiVersion}/companies/${cid}/incomingCalls/filters/?search={"filterBy":"${
              filter.name
            }", "value":${filterValue}}&paginateOptions=${options}${filterDate}&getDeleted=${getDeleted}`,
            { headers },
          );
    return data;
  }

  async outgoingCallRecords(filter, getDeleted = global.deleteMode) {
    const headers = {
      Authorization: `Bearer ${this.api.token}`,
    };
    const data = await axios.get(
      `${this.getBaseURL()}${apiVersion}/calls/outgoingCallsRecord?from=${filter[1]}&to=${
        filter[0]
      }&getDeleted=${getDeleted}`,
      {
        headers,
        responseType: 'blob',
      },
    );
    const file = new Blob([data.data], { type: 'text/csv' });
    const fileURL = URL.createObjectURL(file);
    let a = document.createElement('a');
    a.href = fileURL;
    a.download = `Outgoing Calls - ${format(filter[1], 'MM-DD-YY')} to ${format(
      filter[0],
      'MM-DD-YY',
    )}`;
    a.click();
  }

  async incomingCallRecords(filter, getDeleted = global.deleteMode) {
    const headers = {
      Authorization: `Bearer ${this.api.token}`,
    };
    const { data } = await axios.get(
      `${this.getBaseURL()}${apiVersion}/calls/incomingCallsRecord?from=${filter[1]}&to=${
        filter[0]
      }&getDeleted=${getDeleted}`,
      {
        headers,
        responseType: 'blob',
      },
    );
    const file = new Blob([data.data], { type: 'text/csv' });
    const fileURL = URL.createObjectURL(file);
    let a = document.createElement('a');
    a.href = fileURL;
    a.download = `Incoming Calls - ${format(filter[1], 'MM-DD-YY')} to ${format(
      filter[0],
      'MM-DD-YY',
    )}`;
    a.click();
  }

  async getIncomingCallsByCompany(
    id = null,
    paginateOptions,
    date,
    getDeleted = global.deleteMode,
  ) {
    const headers = {
      Authorization: `Bearer ${this.api.token}`,
    };

    const options = this.getPaginateOptions(paginateOptions);

    const filterDate = date !== 'undefined' ? `&from=${date[1]}&to=${date[0]}` : '';

    const { data } = await axios.get(
      id === null
        ? `${this.getBaseURL()}${apiVersion}/calls/incomingCalls?paginateOptions=${options}${filterDate}&getDeleted=${getDeleted}`
        : `${this.getBaseURL()}${apiVersion}/companies/${id}/incomingCalls?paginateOptions=${options}&${filterDate}&getDeleted=${getDeleted}`,
      { headers },
    );

    return data;
  }

  async downloadOutgoingCalls(id) {
    const headers = {
      Authorization: `Bearer ${this.api.token}`,
    };
    try {
      const res = await axios.get(
        `${this.getBaseURL()}${apiVersion}/calls/${id}/outgoingRecording`,
        {
          headers,
          responseType: 'blob',
        },
      );
      download(res.data, `Call-${id}.wav`, 'audio/wav');
    } catch (error) {
      // handle error
    }
  }

  async downloadIncomingCalls(id) {
    const headers = {
      Authorization: `Bearer ${this.api.token}`,
    };
    try {
      const res = await axios.get(
        `${this.getBaseURL()}${apiVersion}/calls/${id}/incomingRecording`,
        {
          headers,
          responseType: 'blob',
        },
      );
      download(res.data, `Call-${id}.wav`, 'audio/wav');
    } catch (error) {
      // handle error
    }
  }

  callsPlayer(token, id, type) {
    const baseURL = this.getBaseURL();
    return `${baseURL}${apiVersion}/calls/${id}/${type}.wav?token=${token}`;
  }

  async deleteOutgoingCall(id, hardDelete = global.deleteMode ? 'yes' : 'no') {
    const headers = {
      Authorization: `Bearer ${this.api.token}`,
    };
    const { data } = await axios.delete(
      `${this.getBaseURL()}${apiVersion}/calls/outgoingCalls/${id}?hardDelete=${hardDelete}`,
      {
        headers,
      },
    );
    return data;
  }

  async deleteIncomingCall(id, hardDelete = global.deleteMode ? 'yes' : 'no') {
    const headers = {
      Authorization: `Bearer ${this.api.token}`,
    };
    const { data } = await axios.delete(
      `${this.getBaseURL()}${apiVersion}/calls/incomingCalls/${id}?hardDelete=${hardDelete}`,
      {
        headers,
      },
    );
    return data;
  }
}

export default CallsService;
